/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';

// Component imports
import { Button, Col, Form, Row, Space, Tabs, Tooltip, Typography } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import BotDeploymentsGeneralForm from './DeploymentSettings/BotDeploymentsGeneralForm';
import BotDeploymentsWebchatLayoutGeneralForm from './DeploymentSettings/Webchat/BotDeploymentsWebchatLayoutGeneralForm';
import BotDeploymentsWebchatNotificationsForm from './DeploymentSettings/Webchat/BotDeploymentsWebchatNotificationsForm';
import BotDeploymentsWebchatAvatarsForm from './DeploymentSettings/Webchat/BotDeploymentsWebchatAvatarsForm';
import BotDeploymentsWebchatColorsForm from './DeploymentSettings/Webchat/BotDeploymentsWebchatColorsForm';
import BotDeploymentsWebchatActionsForm from './DeploymentSettings/Webchat/BotDeploymentsWebchatActionsForm';
import BotDeploymentsWebchatCustomSettingsForm from './DeploymentSettings/Webchat/BotDeploymentsWebchatCustomSettingsForm';
import BotDeploymentsWebchatSpeechForm from './DeploymentSettings/Webchat/BotDeploymentsWebchatSpeechForm';
import WebchatDeploymentExampleModal from '../Modals/WebchatDeploymentExampleModal';
import { WebchatIcon } from '../Icons/platformIcons/Webchat';

import { CodeIcon } from '../Icons/CodeIcon';

// Misc imports
import { isEmptyArray } from '../../utils/is/isEmptyArray';
import { isEmptyObject } from '../../utils/is/isEmptyObject';
import { BOT_DEPLOYMENTS_CHANNELS, WEBCHAT_WIDGET_MODES, DEFAULT_DATETIME_TIMEZONE_FORMAT } from '../../constants/main';
import { IS_WEBCHAT_DEPLOYMENT_APPLICATION_SETTINGS_ENABLED } from '../../constants/env';
import userHasEditAccess from '../../utils/misc/hasEditAccess';
import { useDeploymentFormErrors } from '../../utils/hooks/useDeploymentFormErrors';
import cloneDeep from 'lodash/cloneDeep';

// Constant declarations
const BUBBLE_ACTIONS = {
  NO_BUBBLE_ACTION: 'No bubble action',
  AUTO_POP_OUT: 'Automatic Pop-out',
  USER_PROMPT_BUBBLE: 'Chat Prompt',
  ADVANCED_CHAT_PROMPT_BUBBLE: 'Advanced Chat Prompt'
};

const POP_OUT_ADVANCED_TIME_SETTINGS = {
  NO_ADVANCED_SETTINGS: 'No advanced time settings',
  DOUBLE_TIME: 'Double Pop-out time after every view',
  ONLY_ONCE: 'Pop-out only once'
};

const DEFAULT_NESTED_TABS_KEY = 'nested_1';

const BotDeploymentsWebchatForm = ({
  formData = {},
  onFormSubmit = () => {},
  userRoles = {},
  isCreateMode = false,
  onPreviewIconClick = () => {},
  onSnippetIconClick = () => {},
  onDeleteClick = () => {},
  isUpdating = false,
  isCreating = false,
  handleBackButton = () => {},
  bodyClassName = '',
  footerClassName = '',
  ...restProps
}) => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const hasEditAccess = isCreateMode ? true : userHasEditAccess(formData?.tenant?.organizationId, userRoles?.organizationRoles, formData?.tenant.id, userRoles?.tenantRoles, true);
  const initialValues = useMemo(() => {
    if(isCreateMode) {
      return {};
    }

    const values = cloneDeep(formData);
    values.settings.layout.microsoftWebChat.configuration.headerStyle = values.settings.layout.microsoftWebChat.configuration.headerStyle || 'center';
    values.hideTypingIndicator= !values.settings.layout.microsoftWebChat.configuration.showTypingIndicator;
    values.fontFamily= values.settings.layout.microsoftWebChat.configuration.fontFamily.split(',')[0] !== '' ? formData.settings.layout.microsoftWebChat.configuration.fontFamily.split(',') : 'Arial';
    values.customSettings= values.settings.layout.microsoftWebChat.configuration.customSettings ? JSON.stringify(formData.settings.layout.microsoftWebChat.configuration.customSettings, null, '\t') : '';

    return values;
  }, [formData]);

  const [applicationSettings, setApplicationSettings] = useState('');
  const [isCustomSettingsEnabled, setIsCustomSettingsEnabled] = useState();
  const [currentDeploymentId, setCurrentDeploymentId] = useState(formData?.id);
  const [isExampleModalVisible, setIsExampleModalVisible] = useState(false);
  const [popOutAdvancedTimeSettingsOption, setPopOutAdvancedTimeSettingsOption] = useState();
  const [bubbleActionSettingsOption, setBubbleActionSettingsOption] = useState();

  const [activeOuterTab, setActiveOuterTab] = useState('1');
  const [activeInnerTab, setActiveInnerTab] = useState(DEFAULT_NESTED_TABS_KEY);

  const {
    errorFields,
    generateTabLabel,
    markAsErroneous,
    setErrorFields
  } = useDeploymentFormErrors();

  useEffect(() => {
    // Reset inner tab active key when navigating to a different outer tab
    setActiveInnerTab(DEFAULT_NESTED_TABS_KEY);
  }, [activeOuterTab]);

  useEffect(() => {
    if (currentDeploymentId !== formData?.id) {
      setCurrentDeploymentId(formData?.id);
      form.resetFields();
    }

    if (IS_WEBCHAT_DEPLOYMENT_APPLICATION_SETTINGS_ENABLED) {
      if (formData?.settings?.integration) {
        setApplicationSettings('customApp');
      } else {
        setApplicationSettings('messageApp');
      }
    } else {
      setApplicationSettings('messageApp');
    }

    if (formData?.settings?.layout?.microsoftWebChat?.configuration?.userPromptBubble?.enable) {
      setBubbleActionSettingsOption(BUBBLE_ACTIONS.USER_PROMPT_BUBBLE);
    } else if (formData?.settings?.layout?.microsoftWebChat?.configuration?.advancedChatPromptBubble?.enable){
      setBubbleActionSettingsOption(BUBBLE_ACTIONS.ADVANCED_CHAT_PROMPT_BUBBLE);
    } else if (formData?.settings?.layout?.microsoftWebChat?.configuration?.autoPopOut?.enable) {
      setBubbleActionSettingsOption(BUBBLE_ACTIONS.AUTO_POP_OUT);
    } else {
      setBubbleActionSettingsOption(BUBBLE_ACTIONS.NO_BUBBLE_ACTION);
    }

    if (formData?.settings?.layout?.microsoftWebChat?.configuration?.autoPopOut?.once) {
      setPopOutAdvancedTimeSettingsOption(POP_OUT_ADVANCED_TIME_SETTINGS.ONLY_ONCE);
    } else if (formData?.settings?.layout?.microsoftWebChat?.configuration?.autoPopOut?.extendTimeAfterView) {
      setPopOutAdvancedTimeSettingsOption(POP_OUT_ADVANCED_TIME_SETTINGS.DOUBLE_TIME);
    } else {
      setPopOutAdvancedTimeSettingsOption(POP_OUT_ADVANCED_TIME_SETTINGS.NO_ADVANCED_SETTINGS);
    }

    if (isCreateMode) {
      setIsCustomSettingsEnabled(false);
    } else {
      if (formData?.settings?.layout?.microsoftWebChat?.configuration?.customSettings) {
        setIsCustomSettingsEnabled(true);
      } else {
        setIsCustomSettingsEnabled(false);
      }
    }

    return () => {
      if (isCreating) {
        form.resetFields();
      }
    };
  }, [formData]);

  // Form handlers
  const onFormSave = (values) => {
    setErrorFields([]);
    const valuesToSubmit = {
      ...values
    };

    // Get the path to startUpNotifications
    const startUpNotifications = valuesToSubmit.settings.layout?.microsoftWebChat?.configuration?.startUpNotifications;
    if (startUpNotifications) {
      // Process each notification
      startUpNotifications.forEach(notification => {
        if (notification) {
          // Remove the enableButtons field - we don't want to include it in the payload
          delete notification.enableButtons;
        }
      });
    }

    // Get the path to startUpNotifications
    const idleNotifications = valuesToSubmit.settings.layout.microsoftWebChat.configuration.idleNotifications;
    if (idleNotifications) {
      // Process each notification
      idleNotifications.forEach(notification => {
        if (notification) {
          // Remove the enableButtons field - we don't want to include it in the payload
          delete notification.enableButtons;
        }
      });
    }

    valuesToSubmit.settings.layout.microsoftWebChat.configuration['fontFamily'] = !isEmptyArray(valuesToSubmit['fontFamily']) && valuesToSubmit['fontFamily']
      ? valuesToSubmit['fontFamily'].toString().trim()
      : 'Arial';

    valuesToSubmit.settings.layout.microsoftWebChat.configuration['customSettings']
      = valuesToSubmit['customSettings'] && isCustomSettingsEnabled && !isEmptyObject(JSON.parse(valuesToSubmit['customSettings']))
        ? JSON.parse(valuesToSubmit['customSettings'])
        : null;

    valuesToSubmit.settings.layout.microsoftWebChat.configuration['showTypingIndicator'] = !valuesToSubmit['hideTypingIndicator'];

    switch (bubbleActionSettingsOption) {
      case BUBBLE_ACTIONS.AUTO_POP_OUT:
        valuesToSubmit.settings.layout.microsoftWebChat.configuration.userPromptBubble = null;
        valuesToSubmit.settings.layout.microsoftWebChat.configuration['autoPopOut'] = {
          ...valuesToSubmit.settings.layout.microsoftWebChat.configuration['autoPopOut'],
          enable: true,
          once: popOutAdvancedTimeSettingsOption === POP_OUT_ADVANCED_TIME_SETTINGS.ONLY_ONCE,
          extendTimeAfterView: popOutAdvancedTimeSettingsOption === POP_OUT_ADVANCED_TIME_SETTINGS.DOUBLE_TIME
        };
        break;
      case BUBBLE_ACTIONS.USER_PROMPT_BUBBLE:
        valuesToSubmit.settings.layout.microsoftWebChat.configuration.autoPopOut = null;
        valuesToSubmit.settings.layout.microsoftWebChat.configuration['userPromptBubble'] = {
          ...valuesToSubmit.settings.layout.microsoftWebChat.configuration['userPromptBubble'],
          enable: bubbleActionSettingsOption === BUBBLE_ACTIONS.USER_PROMPT_BUBBLE
        };
        break;
      case BUBBLE_ACTIONS.ADVANCED_CHAT_PROMPT_BUBBLE:
        valuesToSubmit.settings.layout.microsoftWebChat.configuration.autoPopOut = null;
        valuesToSubmit.settings.layout.microsoftWebChat.configuration['advancedChatPromptBubble'] = {
          ...valuesToSubmit.settings.layout.microsoftWebChat.configuration['advancedChatPromptBubble'],
          enable: bubbleActionSettingsOption === BUBBLE_ACTIONS.ADVANCED_CHAT_PROMPT_BUBBLE
        };
        break;
      default:
        valuesToSubmit.settings.layout.microsoftWebChat.configuration.userPromptBubble = null;
        valuesToSubmit.settings.layout.microsoftWebChat.configuration.autoPopOut = null;
        break;
    }

    if (!valuesToSubmit.settings.layout.microsoftWebChat.configuration.mode) {
      valuesToSubmit.settings.layout.microsoftWebChat.configuration['mode'] = WEBCHAT_WIDGET_MODES.BUBBLE;
    }

    if (isCreateMode) {
      valuesToSubmit['platform'] = BOT_DEPLOYMENTS_CHANNELS.MICROSOFT_WEB_CHAT.key;
      valuesToSubmit['scope'] = 'USER';
      if (applicationSettings === 'messageApp') {
        delete valuesToSubmit.settings['integration'];
      }
    } else {
      if (applicationSettings === 'messageApp') {
        delete valuesToSubmit.settings['integration'];
      }
    }

    onFormSubmit(valuesToSubmit, formData.id, isCreateMode);
  };

  const onFieldError = (fields) => {
    setErrorFields(fields.errorFields || []);

    if (!isEmptyArray(fields.errorFields)) {
      form.scrollToField(fields.errorFields[0].name, { scrollMode: 'if-needed', behavior: 'smooth', block: 'center' });
    }
  };

  const onSnippetButtonClick = (data) => {
    onSnippetIconClick(data);
  };

  const onPreviewButtonClick = (deployment) => {
    onPreviewIconClick(deployment);
  };

  // Modal handlers
  const onExampleModalClose = () => {
    setIsExampleModalVisible(false);
  };

  const onExampleModalVisible = () => {
    setIsExampleModalVisible(true);
  };

  return (
    <Fragment>
      <div className={bodyClassName}>
        <Form
          name="webChatDeploymentForm"
          layout="vertical"
          onFinish={onFormSave}
          onFinishFailed={onFieldError}
          form={form}
          initialValues={initialValues}
          {...restProps}
        >
          <Row>
            <Col flex="auto">
              <Typography.Title level={4} className="mb-2">
                <Space>
                  <WebchatIcon />
                  {isCreateMode ? t('Webchat Deployment') : `${formData.displayName}`}
                </Space>
              </Typography.Title>
            </Col>

            {
              !isCreateMode ? (
                <Col flex="none">
                  <Space>
                    <Tooltip title={t('Preview the deployment')}>
                      <Button
                        size="large"
                        icon={<EyeOutlined />}
                        onClick={() => onPreviewButtonClick(formData)}
                      />
                    </Tooltip>

                    <Tooltip title={t('View the deployment snippet')}>
                      <Button
                        size="large"
                        icon={<CodeIcon />}
                        onClick={() => onSnippetButtonClick(formData)}
                      />
                    </Tooltip>
                  </Space>
                </Col>
              ) : null
            }
          </Row>

          <Tabs
            activeKey={activeOuterTab}
            onChange={setActiveOuterTab}
            items={[
              { key: '1',
                label: generateTabLabel(t('General Settings'), '1'),
                forceRender: true,
                children: <BotDeploymentsGeneralForm
                  form={form}
                  formData={formData}
                  isCreateMode={isCreateMode}
                  onDeleteClick={onDeleteClick}
                  userRoles={userRoles}
                  description={t('Give a name and description to your deployment to make it easy to recognize. For example you can include the name of the webpage it will be added to. This is only for internal purposes.')}
                  hasErrors={errorFields.length}
                  shouldMarkAsErroneous={(isErroneous) => markAsErroneous(isErroneous, '1')}
                  applicationSettings={applicationSettings}
                  setApplicationSettings={setApplicationSettings}
                />
              },
              {
                key: '2',
                label: generateTabLabel(t('Layout Settings'), ['nested_1', 'nested_2', 'nested_3', 'nested_4', 'nested_5', 'nested_6', 'nested_7', 'nested_8']),
                forceRender: true,
                children:
                <>
                  <Row>
                    <Col xs={24}>
                      <Button onClick={onExampleModalVisible} className="float-right" type="link">
                        {t('View Example')}
                      </Button>
                    </Col>
                  </Row>

                  <Tabs
                    activeKey={activeInnerTab}
                    onChange={setActiveInnerTab}
                    tabPosition="left"
                    items={[
                      {
                        key: DEFAULT_NESTED_TABS_KEY,
                        label: t('General'),
                        forceRender: true,
                        children: <BotDeploymentsWebchatLayoutGeneralForm
                          form={form}
                          formData={formData}
                          isCreateMode={isCreateMode}
                          userRoles={userRoles}
                          hasErrors={errorFields.length}
                          shouldMarkAsErroneous={(isErroneous) => markAsErroneous(isErroneous, DEFAULT_NESTED_TABS_KEY)}
                        />
                      },
                      {
                        key: 'nested_2',
                        label: generateTabLabel(t('Start Up Notifications'), 'nested_2'),
                        forceRender: true,
                        children: <BotDeploymentsWebchatNotificationsForm
                          form={form}
                          formData={formData}
                          userRoles={userRoles}
                          isCreateMode={isCreateMode}
                          type="startUpNotifications"
                          hasErrors={errorFields.length}
                          shouldMarkAsErroneous={(isErroneous) => markAsErroneous(isErroneous, 'nested_2')}
                        />
                      },
                      {
                        key: 'nested_3',
                        label: generateTabLabel(t('Idle Notifications'), 'nested_3'),
                        forceRender: true,
                        children: <BotDeploymentsWebchatNotificationsForm
                          form={form}
                          formData={formData}
                          isCreateMode={isCreateMode}
                          userRoles={userRoles}
                          type="idleNotifications"
                          hasErrors={errorFields.length}
                          shouldMarkAsErroneous={(isErroneous) => markAsErroneous(isErroneous, 'nested_3')}
                        />
                      },
                      {
                        key: 'nested_4',
                        label: t('Avatars'),
                        forceRender: true,
                        children: <BotDeploymentsWebchatAvatarsForm
                          form={form}
                          formData={formData}
                          isCreateMode={isCreateMode}
                          userRoles={userRoles}
                          hasErrors={errorFields.length}
                          shouldMarkAsErroneous={(isErroneous) => markAsErroneous(isErroneous, 'nested_4')}
                        />
                      },
                      {
                        key: 'nested_5',
                        label: t('Colors'),
                        forceRender: true,
                        children: <BotDeploymentsWebchatColorsForm
                          form={form}
                          formData={formData}
                          isCreateMode={isCreateMode}
                          userRoles={userRoles}
                          hasErrors={errorFields.length}
                          shouldMarkAsErroneous={(isErroneous) => markAsErroneous(isErroneous, 'nested_5')}
                        />
                      },
                      {
                        key: 'nested_6',
                        label: generateTabLabel(t('Actions'), 'nested_6'),
                        forceRender: true,
                        children: <BotDeploymentsWebchatActionsForm
                          form={form}
                          formData={formData}
                          isCreateMode={isCreateMode}
                          userRoles={userRoles}
                          bubbleActionSettingsOption={bubbleActionSettingsOption}
                          setBubbleActionSettingsOption={setBubbleActionSettingsOption}
                          popOutAdvancedTimeSettingsOption={popOutAdvancedTimeSettingsOption}
                          setPopOutAdvancedTimeSettingsOption={setPopOutAdvancedTimeSettingsOption}
                          hasErrors={errorFields.length}
                          shouldMarkAsErroneous={(isErroneous) => markAsErroneous(isErroneous, 'nested_6')}
                        />
                      },
                      {
                        key: 'nested_7',
                        label: t('Speech'),
                        forceRender: true,
                        children: <BotDeploymentsWebchatSpeechForm
                          form={form}
                          formData={formData}
                          isCreateMode={isCreateMode}
                          userRoles={userRoles}
                        />
                      },
                      {
                        key: 'nested_8',
                        label: generateTabLabel(t('Custom'), 'nested_8'),
                        forceRender: true,
                        children: <BotDeploymentsWebchatCustomSettingsForm
                          form={form}
                          formData={formData}
                          isCreateMode={isCreateMode}
                          userRoles={userRoles}
                          isCustomSettingsEnabled={isCustomSettingsEnabled}
                          setIsCustomSettingsEnabled={setIsCustomSettingsEnabled}
                          hasErrors={errorFields.length}
                          shouldMarkAsErroneous={(isErroneous) => markAsErroneous(isErroneous, 'nested_8')}
                        />
                      }
                    ]}
                  >
                  </Tabs>
                </>
              }
            ]}
          />

        </Form>
      </div>

      <div className={footerClassName}>
        <Row justify="end" gutter={8}>
          <Col>
            {
              errorFields.length ? <p className="text-error">The form contains errors. Please review each tab to correct them.</p> : null
            }
          </Col>
        </Row>
        <Space className="d-flex justify-content-between">
          <Typography.Paragraph
            ellipsis
            className={classNames('text-secondary fs-sm mb-0', { 'opacity-0': isCreateMode })}
          >
            {
              !isCreateMode ? (
                <Fragment>
                  <span className="fw-bold">{t('Created')}:</span> {moment(formData.created).tz(formData.tenant.timezone).format(DEFAULT_DATETIME_TIMEZONE_FORMAT) }<br/>
                  <span className="fw-bold">{t('Last Modified')}:</span> {formData.lastChangedBy?.name ? `${formData.lastChangedBy.name}` : null} {moment(formData.lastChanged).tz(formData.tenant.timezone).format(DEFAULT_DATETIME_TIMEZONE_FORMAT)}<br/>
                </Fragment>
              ) : null
            }
          </Typography.Paragraph>
          <Row justify="end" gutter={8}>
            <Col>
              <Button
                size="large"
                onClick={handleBackButton}
                disabled={isUpdating || isCreating}
              >
                {t('Cancel')}
              </Button>
            </Col>

            <Col>
              <Button
                hidden={!hasEditAccess}
                className="float-right"
                type="primary"
                size="large"
                onClick={ form.submit }
                loading={isUpdating || isCreating}
              >
                {isCreateMode ? t('Create Deployment') : t('Save Changes')}
              </Button>
            </Col>
          </Row>
        </Space>

      </div>

      <WebchatDeploymentExampleModal
        isModalOpen={isExampleModalVisible}
        onModalClose={onExampleModalClose}
      />
    </Fragment>
  );
};

BotDeploymentsWebchatForm.propTypes = {
  formData: PropTypes.object,
  onFormSubmit: PropTypes.func,
  userRoles: PropTypes.object,
  isCreateMode: PropTypes.bool,
  onPreviewIconClick: PropTypes.func,
  onSnippetIconClick: PropTypes.func,
  isUpdating: PropTypes.bool,
  isCreating: PropTypes.bool,
  onDeleteClick: PropTypes.func,
  handleBackButton: PropTypes.func,
  bodyClassName: PropTypes.string,
  footerClassName: PropTypes.string
};

export default BotDeploymentsWebchatForm;
